// import { Checkbox, FormControlLabel } from "@mui/material";
import { FormikProps } from "formik";
import Flex from "../../../../../../../../../components/Flex";
// import Text from "../../../../../../../../../components/Text";
// import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import MicroTextArea from "../../../../../../../../../components/MicroTextArea";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";

import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import {
  AddCptCodes,
  renderSelectedCptCodes,
} from "../components/form-components/Add-CPT-Codes";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import ReferringOutPatientWrapper from "../components/form-components/ReferringOutPatientWrapper";

interface SimpleSoapNoteProps {
  formik: FormikProps<
    Form2Payload & { providerId?: string; providerName?: string }
  >;
}

/**
 *
 * @description: Simple SOAP Note Form
 *
 * @author [Rajkumar Rathod]
 * @returns
 */
const SimpleSoapNote = ({ formik }: SimpleSoapNoteProps) => {
  const [
    icdDisclosure,
    cptDisclosure,
    appointmentDisclosure,
    referralDisclosure,
  ] = useReduceDisclosure(4);
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );

  return (
    <Flex
      gap={2}
      flexDirection="column"
      p={1.5}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Subjective */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Subjective</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="subjectiveFreeNote"
          placeholder="Problems..."
          onBlur={formik.handleBlur}
          value={formik.values?.subjectiveFreeNote || ""}
          onChange={formik.handleChange}
        />
      </Flex>

      {/* Objective */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Objective</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="objectiveFreeNote"
          placeholder="Observations..."
          onBlur={formik.handleBlur}
          value={formik.values?.objectiveFreeNote || ""}
          onChange={formik.handleChange}
        />
      </Flex>

      {/* Assessment */}
      {/* todo : need to add assessment free text to backend API  */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Assessment</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="assessmentFreeNote"
          placeholder="Assessment..."
          onBlur={formik.handleBlur}
          value={formik.values?.assessmentFreeNote || ""}
          onChange={formik.handleChange}
        />

        <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
          <StyleAddButton
            w="120px"
            onClick={() => icdDisclosure.open()}
            label="Add ICD Codes"
          />
          <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
        </Flex>
      </Flex>
      {!!formik.values?.assessmentCodes?.length && (
        <Flex flexDirection={"column"} width={"100%"} gap={1}>
          {/* <Text fontWeight={"bold"}>ICD Codes</Text> */}
          {renderSelectedIcdCodes(
            formik.values?.assessmentCodes ?? [],
            (code) => {
              formik.setFieldValue("assessmentCodes", [
                ...(formik.values?.assessmentCodes ?? []).filter(
                  (it) => it.value !== code.value
                ),
              ]);
            },
            "20vh"
          )}
        </Flex>
      )}

      {/* Plan */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Plan</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="planFreeNote"
          placeholder="Plan..."
          onBlur={formik.handleBlur}
          value={formik.values?.planFreeNote || ""}
          onChange={formik.handleChange}
        />
        <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
          <StyleAddButton
            w="120px"
            onClick={() => cptDisclosure.open()}
            label="Add CPT Codes"
          />
          <StyleAddButton
            w="120px"
            onClick={() => {
              referralDisclosure.open();
            }}
            label="Referral Out"
          />
          <AddCptCodes formik={formik} disclosureHost={cptDisclosure} />

          <ReferringOutPatientWrapper
            referralDisclosure={referralDisclosure}
            referralDiagnosisCode={formik.values?.assessmentCodes?.map(
              (it) => ({ value: it.value, label: it.code })
            )}
          />
        </Flex>
      </Flex>
      {!!formik.values?.procedureCodes?.length && (
        <Flex flexDirection={"column"} width={"100%"} gap={1}>
          {/* <Text fontWeight={"bold"}>CPT Codes</Text> */}
          {renderSelectedCptCodes(
            formik.values?.procedureCodes ?? [],
            (code) => {
              formik.setFieldValue("procedureCodes", [
                ...(formik.values?.procedureCodes ?? []).filter(
                  (it) => it.value !== code.value
                ),
              ]);
            },
            "20vh"
          )}
        </Flex>
      )}
      {/* Follow Up & Instructions */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Follow Up & Instructions</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="followUp"
          placeholder="Follow Up & Instructions..."
          onBlur={formik.handleBlur}
          value={formik.values?.followUp || ""}
          onChange={formik.handleChange}
        />

        {hasUpdateAppointment && (
          <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <StyleAddButton
              w="120px"
              onClick={() => {
                appointmentDisclosure.open();
              }}
              label="Schedule F/U Appointment"
            />
            {appointmentDisclosure.opened && (
              <NewAppointment
                enablePrefillPatientData
                opened={appointmentDisclosure.opened}
                onClose={appointmentDisclosure.close}
              />
            )}
          </Flex>
        )}
        {/* <FormControlLabel
          control={
            <Checkbox
              name="psychoeducationSessionRequired"
              checked={!!formik.values?.psychoeducationSessionRequired}
              onChange={(e) =>
                formik.setFieldValue(
                  "psychoeducationSessionRequired",
                  e.target.checked
                )
              }
            />
          }
          label={
            <Text variant="body1" fontWeight={500}>
              Psychoeducation Session Required?
            </Text>
          }
        />
        {formik.values?.psychoeducationSessionRequired && (
          <PsychoeducationTherapyForm formik={formik} />
        )} */}
      </Flex>
    </Flex>
  );
};

export default SimpleSoapNote;
